import { Button } from "@/components/ui/Button";
import { compressImageFileToDataUrl } from "@/lib/imageUpload";
import { getDesignProposal, INFO_SCREEN_DESIGNS } from "@/pages/info-screen/designs";
import type {
  InfoScreenDesign,
  InfoScreenImage,
  InfoScreenImageTextOverlay,
  InfoScreenLayout,
  InfoScreenPage,
  InfoScreenTextBlock,
  InfoScreenTextVariant,
} from "@/types/infoScreen";

const LAYOUT_OPTIONS: { value: InfoScreenLayout; label: string }[] = [
  { value: "split", label: "Diashow + Text (Standard)" },
  { value: "centered", label: "Nur Text, zentriert" },
  { value: "columns", label: "Zwei Spalten" },
  { value: "full_bleed", label: "Vollflächiges Bild mit Text" },
  { value: "image_focus", label: "Bild im Fokus" },
];

const VARIANT_OPTIONS: { value: InfoScreenTextVariant; label: string }[] = [
  { value: "headline", label: "Überschrift" },
  { value: "body", label: "Fließtext" },
  { value: "highlight", label: "Hervorhebung" },
  { value: "hint", label: "Hinweis" },
];

const OVERLAY_POSITION_PRESETS: {
  value: string;
  label: string;
  x_percent: number;
  y_percent: number;
}[] = [
  { value: "bottom-left", label: "Unten links", x_percent: 18, y_percent: 88 },
  { value: "bottom-center", label: "Unten Mitte", x_percent: 50, y_percent: 88 },
  { value: "bottom-right", label: "Unten rechts", x_percent: 82, y_percent: 88 },
  { value: "center", label: "Mitte", x_percent: 50, y_percent: 50 },
  { value: "top-center", label: "Oben Mitte", x_percent: 50, y_percent: 15 },
];

function defaultImageOverlay(): InfoScreenImageTextOverlay {
  return {
    title: null,
    body: "",
    variant: "body",
    x_percent: 50,
    y_percent: 88,
  };
}

function overlayPresetValue(overlay: InfoScreenImageTextOverlay): string {
  const match = OVERLAY_POSITION_PRESETS.find(
    (p) => p.x_percent === overlay.x_percent && p.y_percent === overlay.y_percent,
  );
  return match?.value ?? "custom";
}

function newId(): string {
  return crypto.randomUUID();
}

type Props = {
  page: InfoScreenPage;
  canEdit: boolean;
  onChange: (page: InfoScreenPage) => void;
};

export function InfoScreenPageEditor({ page, canEdit, onChange }: Props) {
  function patch(partial: Partial<InfoScreenPage>) {
    onChange({ ...page, ...partial });
  }

  async function onImageSelected(file: File | null, column?: InfoScreenImage["column"]) {
    if (!file || !canEdit) return;
    if (!file.type.startsWith("image/")) return;
    const imageData = await compressImageFileToDataUrl(file);
    const next: InfoScreenImage = {
      id: newId(),
      image_url: imageData,
      alt_text: file.name.replace(/\.[^.]+$/, ""),
      caption: null,
      sort_order: page.images.length * 10 + 10,
      column: page.layout === "columns" ? (column ?? null) : null,
    };
    patch({ images: [...page.images, next] });
  }

  function updateTextBlock(
    listKey: "text_blocks" | "column_left_blocks" | "column_right_blocks",
    id: string,
    partial: Partial<InfoScreenTextBlock>,
  ) {
    patch({
      [listKey]: page[listKey].map((b) => (b.id === id ? { ...b, ...partial } : b)),
    } as Partial<InfoScreenPage>);
  }

  function addTextBlock(listKey: "text_blocks" | "column_left_blocks" | "column_right_blocks") {
    const block: InfoScreenTextBlock = {
      id: newId(),
      title: null,
      body: "",
      variant: "body",
      sort_order: page[listKey].length * 10 + 10,
      column: listKey === "column_left_blocks" ? "left" : listKey === "column_right_blocks" ? "right" : null,
    };
    patch({ [listKey]: [...page[listKey], block] } as Partial<InfoScreenPage>);
  }

  function removeTextBlock(
    listKey: "text_blocks" | "column_left_blocks" | "column_right_blocks",
    id: string,
  ) {
    patch({ [listKey]: page[listKey].filter((b) => b.id !== id) } as Partial<InfoScreenPage>);
  }

  function updateImage(imageId: string, partial: Partial<InfoScreenImage>) {
    patch({
      images: page.images.map((i) => (i.id === imageId ? { ...i, ...partial } : i)),
    });
  }

  function updateImageOverlay(imageId: string, partial: Partial<InfoScreenImageTextOverlay>) {
    const img = page.images.find((i) => i.id === imageId);
    if (!img) return;
    const current = img.text_overlay ?? defaultImageOverlay();
    updateImage(imageId, { text_overlay: { ...current, ...partial } });
  }

  function ensureImageOverlay(imageId: string) {
    const img = page.images.find((i) => i.id === imageId);
    if (!img || img.text_overlay) return;
    updateImage(imageId, { text_overlay: defaultImageOverlay() });
  }

  function renderTextBlockList(
    title: string,
    listKey: "text_blocks" | "column_left_blocks" | "column_right_blocks",
  ) {
    const blocks = [...page[listKey]].sort((a, b) => a.sort_order - b.sort_order);
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium">{title}</h4>
        {blocks.map((block) => (
          <div key={block.id} className="space-y-3 rounded-xl border border-separator bg-fill/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex flex-col gap-1 text-sm">
                Darstellung
                <select
                  disabled={!canEdit}
                  className="eh-input w-44"
                  value={block.variant}
                  onChange={(e) =>
                    updateTextBlock(listKey, block.id, {
                      variant: e.target.value as InfoScreenTextVariant,
                    })
                  }
                >
                  {VARIANT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {canEdit ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => removeTextBlock(listKey, block.id)}
                >
                  Entfernen
                </Button>
              ) : null}
            </div>
            <label className="flex flex-col gap-1 text-sm">
              Optionaler Titel
              <input
                disabled={!canEdit}
                className="eh-input"
                value={block.title || ""}
                onChange={(e) =>
                  updateTextBlock(listKey, block.id, { title: e.target.value || null })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Text
              <textarea
                disabled={!canEdit}
                className="eh-input min-h-[5rem]"
                value={block.body}
                onChange={(e) => updateTextBlock(listKey, block.id, { body: e.target.value })}
              />
            </label>
          </div>
        ))}
        {canEdit ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => addTextBlock(listKey)}>
            Textfeld hinzufügen
          </Button>
        ) : null}
      </div>
    );
  }

  const isFullBleedLayout = page.layout === "full_bleed" || page.layout === "image_focus";
  const showMainText = page.layout !== "columns" && !isFullBleedLayout;
  const showColumns = page.layout === "columns";
  const showImages = page.layout !== "centered";
  const showSlideshowInterval = showImages && !isFullBleedLayout;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          Seitenname (intern)
          <input
            disabled={!canEdit}
            className="eh-input"
            value={page.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Design
          <select
            disabled={!canEdit}
            className="eh-input"
            value={page.design ?? "classic"}
            onChange={(e) => {
              const design = e.target.value as InfoScreenDesign;
              const proposal = getDesignProposal(design);
              patch({
                design,
                layout:
                  page.layout === "columns" && proposal.layout !== "columns"
                    ? proposal.layout
                    : page.layout,
              });
            }}
          >
            {INFO_SCREEN_DESIGNS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Layout
          <select
            disabled={!canEdit}
            className="eh-input"
            value={page.layout}
            onChange={(e) => patch({ layout: e.target.value as InfoScreenLayout })}
          >
            {LAYOUT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-label-secondary sm:col-span-2">
          {getDesignProposal(page.design ?? "classic").description}
        </p>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={page.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Seite aktiv (wird auf dem Bildschirm angezeigt)
        </label>
        {showSlideshowInterval ? (
          <label className="flex flex-col gap-1 text-sm">
            Bildwechsel auf dieser Seite (Sekunden)
            <input
              type="number"
              min={3}
              max={60}
              disabled={!canEdit}
              className="eh-input w-28"
              value={page.slideshow_interval_seconds}
              onChange={(e) =>
                patch({
                  slideshow_interval_seconds: Math.min(
                    60,
                    Math.max(3, Number(e.target.value) || 8),
                  ),
                })
              }
            />
          </label>
        ) : null}
        {isFullBleedLayout ? (
          <p className="text-sm text-label-secondary sm:col-span-2">
            Bei mehreren Bildern wechselt die Anzeige beim erneuten Einblenden dieser Seite zum
            nächsten Bild (gesteuert über die globale Seitenrotation, kein eigener Bild-Timer).
          </p>
        ) : null}
      </div>

      {showImages ? (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Bilder</h4>
          {showColumns ? (
            <p className="text-sm text-label-secondary">
              Weisen Sie Bilder einer Spalte zu oder lassen Sie die Zuordnung leer für ein
              Querformat-Banner über beiden Spalten. Pro Spalte wechseln mehrere Bilder automatisch.
            </p>
          ) : null}
          {page.images.length > 0 ? (
            <ul className="grid gap-3 sm:grid-cols-2">
              {page.images
                .slice()
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((img) => {
                  const overlay = img.text_overlay ?? defaultImageOverlay();
                  const showOverlayEditor = isFullBleedLayout;
                  return (
                  <li key={img.id} className="space-y-3 rounded-lg border border-separator p-3">
                    <div className="relative aspect-video w-full overflow-hidden rounded-md bg-fill">
                      <img
                        src={img.image_url}
                        alt={img.alt_text || ""}
                        className="h-full w-full object-cover"
                      />
                      {showOverlayEditor &&
                      (overlay.title?.trim() || overlay.body?.trim()) ? (
                        <div
                          className="pointer-events-none absolute max-w-[70%] -translate-x-1/2 -translate-y-1/2 rounded bg-black/55 px-2 py-1 text-xs text-white"
                          style={{
                            left: `${overlay.x_percent}%`,
                            top: `${overlay.y_percent}%`,
                          }}
                        >
                          {overlay.title ? (
                            <p className="font-semibold">{overlay.title}</p>
                          ) : null}
                          {overlay.body ? (
                            <p className="line-clamp-3 whitespace-pre-line">{overlay.body}</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {showColumns ? (
                      <label className="flex flex-col gap-1 text-xs">
                        Anzeige im Layout
                        <select
                          disabled={!canEdit}
                          className="eh-input"
                          value={img.column ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateImage(img.id, {
                              column:
                                value === "left" || value === "right"
                                  ? value
                                  : null,
                            });
                          }}
                        >
                          <option value="">Banner oben (beide Spalten)</option>
                          <option value="left">Linke Spalte</option>
                          <option value="right">Rechte Spalte</option>
                        </select>
                      </label>
                    ) : null}
                    <label className="flex flex-col gap-1 text-xs">
                      Bildunterschrift
                      <input
                        disabled={!canEdit}
                        className="eh-input"
                        value={img.caption || ""}
                        onChange={(e) =>
                          updateImage(img.id, { caption: e.target.value || null })
                        }
                      />
                    </label>
                    {showOverlayEditor ? (
                      <div className="space-y-3 rounded-lg border border-dashed border-separator bg-fill/20 p-3">
                        <h5 className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                          Text auf dem Bild
                        </h5>
                        <label className="flex flex-col gap-1 text-xs">
                          Darstellung
                          <select
                            disabled={!canEdit}
                            className="eh-input"
                            value={overlay.variant}
                            onChange={(e) => {
                              ensureImageOverlay(img.id);
                              updateImageOverlay(img.id, {
                                variant: e.target.value as InfoScreenTextVariant,
                              });
                            }}
                          >
                            {VARIANT_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          Optionaler Titel
                          <input
                            disabled={!canEdit}
                            className="eh-input"
                            value={overlay.title || ""}
                            onChange={(e) => {
                              ensureImageOverlay(img.id);
                              updateImageOverlay(img.id, { title: e.target.value || null });
                            }}
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          Text
                          <textarea
                            disabled={!canEdit}
                            className="eh-input min-h-[4rem]"
                            value={overlay.body}
                            onChange={(e) => {
                              ensureImageOverlay(img.id);
                              updateImageOverlay(img.id, { body: e.target.value });
                            }}
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          Position (Vorgabe)
                          <select
                            disabled={!canEdit}
                            className="eh-input"
                            value={overlayPresetValue(overlay)}
                            onChange={(e) => {
                              const preset = OVERLAY_POSITION_PRESETS.find(
                                (p) => p.value === e.target.value,
                              );
                              if (!preset) return;
                              ensureImageOverlay(img.id);
                              updateImageOverlay(img.id, {
                                x_percent: preset.x_percent,
                                y_percent: preset.y_percent,
                              });
                            }}
                          >
                            {OVERLAY_POSITION_PRESETS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                            <option value="custom">Individuell</option>
                          </select>
                        </label>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="flex flex-col gap-1 text-xs">
                            Horizontal ({Math.round(overlay.x_percent)} %)
                            <input
                              type="range"
                              min={0}
                              max={100}
                              disabled={!canEdit}
                              value={overlay.x_percent}
                              onChange={(e) => {
                                ensureImageOverlay(img.id);
                                updateImageOverlay(img.id, {
                                  x_percent: Number(e.target.value),
                                });
                              }}
                            />
                          </label>
                          <label className="flex flex-col gap-1 text-xs">
                            Vertikal ({Math.round(overlay.y_percent)} %)
                            <input
                              type="range"
                              min={0}
                              max={100}
                              disabled={!canEdit}
                              value={overlay.y_percent}
                              onChange={(e) => {
                                ensureImageOverlay(img.id);
                                updateImageOverlay(img.id, {
                                  y_percent: Number(e.target.value),
                                });
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    ) : null}
                    {canEdit ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          patch({ images: page.images.filter((i) => i.id !== img.id) })
                        }
                      >
                        Bild entfernen
                      </Button>
                    ) : null}
                  </li>
                  );
                })}
            </ul>
          ) : (
            <p className="text-sm text-label-tertiary italic">Noch keine Bilder.</p>
          )}
          {canEdit ? (
            showColumns ? (
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-separator bg-fill/40 px-3 py-2 text-sm font-medium text-accent">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null, null)}
                  />
                  Banner-Bild
                </label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-separator bg-fill/40 px-3 py-2 text-sm font-medium text-accent">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null, "left")}
                  />
                  Bild linke Spalte
                </label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-separator bg-fill/40 px-3 py-2 text-sm font-medium text-accent">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null, "right")}
                  />
                  Bild rechte Spalte
                </label>
              </div>
            ) : (
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-accent">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null)}
                />
                Bild hinzufügen
              </label>
            )
          ) : null}
        </div>
      ) : null}

      {showMainText ? renderTextBlockList("Textfelder", "text_blocks") : null}
      {showColumns ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {renderTextBlockList("Linke Spalte", "column_left_blocks")}
          {renderTextBlockList("Rechte Spalte", "column_right_blocks")}
        </div>
      ) : null}
    </div>
  );
}
